/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import { ReactComponent as LeftArrow } from "../../assets/icons/arrows/left-arrow.svg";
import {
  FormInput,
  FormTwoInputs,
  OrangeButton,
  PrimaryButton,
  RedButton,
  SecondaryButton,
} from "../../components";
import { useEffect, useMemo, useRef, useState } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useDrops } from "../../store/useDrops";
import { useMarketPricing } from "../../store/useMarketPricing";
import { LOG } from "../../utils/logUtil";
import { useNotifications } from "../../store/useNotifications";
import { useUploadImage } from "../../store/useUploadImage";
import { Tooltip } from "@material-tailwind/react";
import { editedLinePrice } from "../../utils/locationRates";

function DropDetailsScreen() {
  /** Refs */
  const fileInputRef = useRef(null);
  /** Hooks */
  const navigate = useNavigate();
  const params = useParams();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  const {
    getOneDrop,
    oneDrop,
    updateDrop,
    updatedDrop,
    clearUpdatedDrop,
    createDropFeedback,
  } = useDrops();
  const { getNotifications, notifications } = useNotifications();
  const { uploadImage, clearUploadedImage, uploadedImage } = useUploadImage();
  /** State */
  const [dropCompositions, setDropCompositions] = useState<any[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [comment, setComment] = useState<string>("");
  const [dropQrCode, setDropQrCode] = useState<string | null>(null);

  /** Effects */
  useEffect(() => {
    if (!params?.id) return;
    getAllMarketPrices();
    getOneDrop(params?.id);
    //Check if notifications contains drop id
    const notificationExist = notifications?.some(
      (notification) => notification?.id === params?.id
    );
    if (notificationExist) {
      getNotifications();
    }
  }, [params?.id]);

  useEffect(() => {
    setDropCompositions(oneDrop?.dropComposition ?? []);
    setTotalPrice(oneDrop?.totalPrice ?? 0);
    setTotalWeight(oneDrop?.totalWeight ?? 0);
  }, [oneDrop]);

  useEffect(() => {
    if (!dropCompositions?.length) return;
    // Only kg-measured materials are kilograms. Statiegeld and unit materials
    // put a piece count in the same `weight` field, so adding the two together
    // is what makes a 210-bottle drop read as 210 kg.
    const newTotalWeight = dropCompositions?.reduce(
      (acc, curr) =>
        curr?.type?.measurement === "kg" ? acc + Number(curr?.weight ?? 0) : acc,
      0
    );
    const newTotalItems = dropCompositions?.reduce(
      (acc, curr) =>
        curr?.type?.measurement === "kg" ? acc : acc + Number(curr?.weight ?? 0),
      0
    );
    const newTotalPrice = dropCompositions?.reduce(
      (acc, curr) => acc + Number(curr?.price ?? 0),
      0
    );
    setTotalWeight(newTotalWeight);
    setTotalItems(newTotalItems);
    setTotalPrice(newTotalPrice);
  }, [dropCompositions]);

  useEffect(() => {
    if (updatedDrop) {
      clearUpdatedDrop();
      navigate(-1);
    }
  }, [updatedDrop]);

  useEffect(() => {
    if (oneDrop?.qrCode) {
      setDropQrCode(oneDrop?.qrCode);
    } else {
      generateQRCode();
    }
  }, [oneDrop?.qrCode]);

  /** Callbacks */
  const handleUploadClick = () => {
    if (fileInputRef?.current) {
      // @ts-ignore
      fileInputRef?.current?.click();
    }
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      uploadImage(e.target.files[0]);
    }
  };

  const generateQRCode = async () => {
    try {
      const response = await fetch(
        `https://api.qrserver.com/v1/create-qr-code/?data=drop/${oneDrop?._id}&size=220x220`
      );
      const jsonResponse = await response.blob();
      //Generate blob
      // @ts-ignore
      var blob = new Blob([jsonResponse], {
        type: "text/vtt; charset=utf-8",
      });

      const fileReaderInstance = new FileReader();
      fileReaderInstance.readAsDataURL(blob);
      fileReaderInstance.onload = () => {
        setDropQrCode(fileReaderInstance.result as string);
      };
      // setUserQrCode(URL.createObjectURL(jsonResponse));
    } catch (error) {
      LOG.error("Error on generating QR code", { error });
    }
  };

  const handlePrintQrCode = () => {
    const printWindow = window.open("", "_blank");
    // printWindow?.document?.write(
    //   "<html><head><title>Print QR Code</title></head><body>"
    // );
    printWindow?.document?.write(
      `<img src="${dropQrCode}" style="width:100%; max-width:300px;"/>`
    );
    printWindow?.document.write("</body></html>");
    printWindow?.document.close();
    printWindow?.focus();
    printWindow?.print();
    printWindow?.close();
  };

  const goBack = () => navigate(-1);

  const dropLocationId: string | null =
    // @ts-ignore — populated location or a bare id
    oneDrop?.location?._id ?? (oneDrop?.location as any) ?? null;

  function splitArray(arr: any[]) {
    const middle = Math.floor(arr.length / 2);
    return [arr.slice(0, middle + 1), arr.slice(middle + 1)];
  }

  const compositions = useMemo(() => {
    return splitArray(dropCompositions ?? []);
  }, [dropCompositions]);

  const approveDrop = () => {
    const body = { status: "approved", dropComposition: dropCompositions };
    // @ts-ignore
    updateDrop(oneDrop?._id as string, body);
  };
  const holdDrop = () => {
    const body = { status: "pending", dropComposition: dropCompositions };
    // @ts-ignore
    updateDrop(oneDrop?._id as string, body);
  };
  const rejectDrop = () => {
    const body = { status: "declined", dropComposition: dropCompositions };
    // @ts-ignore
    updateDrop(oneDrop?._id as string, body);
  };

  const openUserDetails = () => {
    navigate(`/edit-user/${oneDrop?.user?._id}`);
  };

  const addComment = () => {
    if (comment === "") return;
    setComment("");
    clearUploadedImage();
    createDropFeedback(
      oneDrop?._id as string,
      comment,
      uploadedImage as string
    );
  };

  const handleComment = (e: any) => setComment(e?.target?.value);

  const onSubmit = () => {
    let newDropCompositions = dropCompositions?.map((dc) => ({
      ...dc,
      type: dc?.type?._id,
    }));
    const body = { dropComposition: newDropCompositions };
    updateDrop(oneDrop?._id as string, body);
  };

  return (
    <div>
      <div className="flex flex-row items-start sm:items-center mb-6 justify-between">
        <div className="flex flex-row items-center justify-start">
          <LeftArrow onClick={goBack} className="mr-3 cursor-pointer" />
          <h1 className="font-satoshi text-sm sm:text-2xl font-bold text-dark">
            #{params?.id}
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row items-end sm:items-center justify-center sm:justify-end">
          <PrimaryButton onClick={approveDrop} label="Approve" />
          <OrangeButton onClick={holdDrop} className="sm:ml-4" label="Hold" />
          <RedButton onClick={rejectDrop} className="sm:ml-4" label="Reject" />
        </div>
      </div>
      <div className="w-full flex flex-col sm:flex-row mb-2 sm:mb-6">
        <div className="w-full sm:w-1/2 min-w-fit h-fit p-7 bg-white border border-gray-border drop-shadow-md mb-[10px] sm:mb-[20px] flex flex-row justify-between">
          <div className="flex flex-row items-center">
            <img
              alt="profileImage"
              className="w-[60px] sm:w-[100px] h-[60px] sm:h-[100px] rounded-full object-cover mr-8"
              src={
                oneDrop?.user?.profileImage ??
                require("../../assets/pictures/dummy/dummy-avatar.png")
              }
            />
            <p className="self-center font-satoshi text-dark font-bold text-base sm:text-xl">
              {oneDrop?.user?.first_name} {oneDrop?.user?.last_name}
            </p>
          </div>
          <div className="self-center">
            <PrimaryButton onClick={openUserDetails} label="User details" />
          </div>
        </div>
        <div className="w-full sm:w-1/2 min-w-96 sm:ml-8 bg-white border border-gray-border drop-shadow-md">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px] flex flex-row justify-between items-center">
            <p className="font-satoshi font-medium text-base text-dark">
              QR Code
            </p>
            <SecondaryButton
              onClick={handlePrintQrCode}
              label="Print QR Code"
              className="h-10"
            />
          </div>
          <div className="flex w-full px-7 py-7 justify-center items-center">
            {dropQrCode ? (
              <img
                className="w-[250px] h-[250px] object-cover"
                alt="profile_picture"
                src={dropQrCode ?? undefined}
              />
            ) : (
              <p className="font-satoshi text-base text-dark">No QR code</p>
            )}
          </div>
        </div>
      </div>
      <div className="w-full min-w-fit p-2 sm:p-7 bg-white border border-gray-border drop-shadow-md mb-[10px] sm:mb-[20px]">
        <div className="w-full bg-gray-50 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-medium text-base">
          <p>Weight</p>
          <p>Earnings</p>
        </div>
        <div className="w-full bg-white px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
          <p>
            {[
              totalWeight ? `${totalWeight} kg` : null,
              totalItems ? `${totalItems} items` : null,
            ]
              .filter(Boolean)
              .join(", ") || "0 kg"}
          </p>
          <p>
            {/* {Number(totalPrice ? totalPrice / 100 : 0)?.toFixed(2)} € */}
            {Number(totalPrice ? totalPrice / 100 : 0)?.toFixed(2)} €
          </p>
        </div>
      </div>
      <div className="w-full min-w-fit p-2 sm:p-7 bg-white border border-gray-border drop-shadow-md mb-[10px] sm:mb-[20px]">
        <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
          Comments
        </p>
        {oneDrop?.dropFeedback?.map((feedback) => (
          <div
            className={`border border-gray-border p-2 rounded mb-2 ${feedback?.user?._id === oneDrop?.user?._id ? "bg-gray-200" : ""
              }`}
          >
            <div className="flex flex-row items-center">
              <img
                alt="profileImage"
                src={
                  feedback?.user?.profileImage ??
                  require("../../assets/pictures/dummy/dummy-avatar.png")
                }
                className="h-11 w-11 rounded-full"
              />
              <p className="font-satoshi font-medium text-base ml-4">
                {feedback?.user?.first_name} {feedback?.user?.last_name}
              </p>
            </div>
            <FormInput
              placeholder="Add comment"
              value={feedback?.comment}
              disabled
              containerClassname="w-full"
            />
            {feedback?.image ? (
              <div className="w-fit rounded p-2 mt-2 mr-2">
                <img
                  src={feedback?.image}
                  alt="uploaded"
                  className="max-w-fit max-h-[200px] object-cover"
                />
              </div>
            ) : null}
          </div>
        ))}
        <div className="flex flex-col sm:flex-row h-auto sm:h-[48px] w-full">
          <input
            value={comment}
            onChange={handleComment}
            type="text"
            className={`flex-1 min-h-[46px] px-2 sm:px-4 bg-sky-lighter border border-gray-border focus:border-focused-border rounded font-satoshi text-base text-dark font-medium outline-none mb-1 sm:mb-0`}
            placeholder="Add comment here..."
          />
          <div
            onClick={handleUploadClick}
            className="flex flex-row items-center justify-center py-2 sm:py-0 mb-2 sm:mb-0 sm:mx-2 sm:px-4 border border-gray-border hover:border-focused-border rounded bg-sky-lighter hover:bg-sky-light transition-all ease-linear duration-200 cursor-pointer"
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".svg, .png, .jpg, .gif"
              onChange={onImageChange}
            />
            <img
              alt="upload-icon-v2"
              src={require("../../assets/icons/form/upload.png")}
              className="w-5 h-5"
            />
          </div>
          <PrimaryButton
            onClick={addComment}
            label="Add comment"
            disabled={comment === "" || !comment}
          />
        </div>
        {uploadedImage ? (
          <div className="border p-2 border-gray-border mt-2 w-fit">
            <Tooltip content="Delete">
              <img
                src={uploadedImage}
                alt="uploaded"
                onClick={clearUploadedImage}
                className="max-w-fit max-h-[200px] object-cover cursor-pointer hover:grayscale transition-all ease-linear duration-200"
              />
            </Tooltip>
          </div>
        ) : null}
      </div>
      <div className="w-full flex flex-col sm:flex-row mb-6">
        {compositions?.[0]?.length ? (
          <div className="w-full sm:w-1/2 min-w-fit p-7 bg-white border border-gray-border drop-shadow-md sm:mr-2">
            {compositions?.[0]?.map((composition: any) => {
              const compositionName =
                marketPrices?.find(
                  (mp: any) => mp?._id === composition?.type?._id
                )?.name ?? "";
              return (
                <FormTwoInputs
                  key={composition?._id}
                  label={compositionName}
                  leftPlaceholder={
                    composition?.type?.measurement === "kg" ? "Weight" : "Items"
                  }
                  leftValue={composition?.weight}
                  rightPlaceholder="€"
                  rightValue={new Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: "EUR"
                  }).format(Number(composition?.price ? composition?.price / 100 : 0))}
                  rightDisabled
                  leftOnChange={(e) => {
                    const newValue = e?.target?.value?.replaceAll(",", ".");
                    if (!isNaN(Number(newValue))) {
                      const updatedCompositions = dropCompositions.map((dc) => {
                        if (dc._id === composition._id) {
                          // What the backend stores for this edit: priced at
                          // the rate this line was paid at.
                          const price = editedLinePrice(
                            oneDrop?.dropComposition?.find(
                              (line) => line?._id === composition?._id
                            ),
                            Number(newValue ?? 0),
                            marketPrices?.find(
                              (mpItem) =>
                                mpItem?._id === composition?.type?._id
                            ),
                            dropLocationId
                          );
                          return {
                            ...dc,
                            weight: newValue,
                            price,
                          };
                        }
                        return dc;
                      });
                      setDropCompositions(updatedCompositions);
                    }
                  }}
                />
              );
            })}
          </div>
        ) : null}

        {compositions?.[1]?.length ? (
          <div className="w-full sm:w-1/2 min-w-fit p-7 bg-white border border-gray-border drop-shadow-md sm:mr-2">
            {compositions?.[1]?.map((composition: any) => {
              const compositionName =
                marketPrices?.find(
                  (mp: any) => mp?._id === composition?.type?._id
                )?.name ?? "";
              return (
                <FormTwoInputs
                  key={composition?._id}
                  label={compositionName}
                  leftPlaceholder={
                    composition?.type?.measurement === "kg" ? "Weight" : "Items"
                  }
                  leftValue={composition?.weight}
                  rightPlaceholder="€"
                  rightValue={new Intl.NumberFormat("nl-NL", {
                    style: "currency",
                    currency: "EUR"
                  }).format(Number(composition?.price ? composition?.price / 100 : 0))}
                  leftOnChange={(e) => {
                    const newValue = e?.target?.value?.replaceAll(",", ".");
                    if (!isNaN(Number(newValue))) {
                      const updatedCompositions = dropCompositions.map((dc) => {
                        if (dc._id === composition._id) {
                          // What the backend stores for this edit: priced at
                          // the rate this line was paid at.
                          const price = editedLinePrice(
                            oneDrop?.dropComposition?.find(
                              (line) => line?._id === composition?._id
                            ),
                            Number(newValue ?? 0),
                            marketPrices?.find(
                              (mpItem) =>
                                mpItem?._id === composition?.type?._id
                            ),
                            dropLocationId
                          );
                          return {
                            ...dc,
                            weight: newValue,
                            price,
                          };
                        }
                        return dc;
                      });
                      setDropCompositions(updatedCompositions);
                    }
                  }}
                  rightDisabled
                />
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="flex flex-row items-center justify-end">
        <SecondaryButton onClick={goBack} label="Cancel" />
        <PrimaryButton onClick={onSubmit} className="ml-4" label="Save" />
      </div>
    </div>
  );
}

export default withProtectedRoute(DropDetailsScreen);
